import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import PlacementAnalyticsService, { FileType, MatrixType, TemplateType } from './PlacementAnalyticsService';

interface Props {
    instituteId: number | string;
}

interface AxisRow {
    id?: number;
    placementProgramId?: number | null;
    matrixType: MatrixType;
    xThreshold: number;
    xMax: number;
    yMax: number;
    yThreshold: number;
    programDisplayName?: string | null;
    programShortCode?: string | null;
}

interface ProgramOpt {
    id: number | null;
    placementProgramId?: number | null;
    programDisplayName?: string;
    programShortCode?: string | null;
    isAllPrograms?: boolean;
}

const AXIS_DEFAULTS: Record<MatrixType, { xThreshold: number; xMax: number; yMax: number; yThreshold: number }> = {
    AHLI: { xThreshold: 0.45, xMax: 0, yMax: 90, yThreshold: 40 },
    SPM: { xThreshold: 5, xMax: 0, yMax: 0, yThreshold: 13 },
};

function validateAxisLocal(
    matrixType: MatrixType,
    x: number,
    xMax: number,
    yMax: number,
    yTh: number
): string | null {
    if (![x, xMax, yMax, yTh].every(Number.isFinite)) return 'All thresholds must be numbers';
    if (matrixType === 'AHLI') {
        if (x < 0 || x > 1) return 'AHLI xThreshold must be between 0 and 1 (e.g. 0.3 = 30%)';
        if (xMax < 0) return 'AHLI xMax must be ≥ 0 (0 = no cap)';
        if (yMax <= 0) return 'AHLI yMax must be > 0';
        if (yTh > yMax / 2 || yTh < yMax / 4) {
            return `AHLI yThreshold must be ≤ ${yMax / 2} and ≥ ${yMax / 4}`;
        }
    } else {
        if (x <= 0 || !Number.isInteger(x)) return 'SPM xThreshold must be a positive integer';
        if (xMax < 0) return 'SPM xMax must be ≥ 0 (0 = no cap)';
        if (yMax < 0) return 'SPM yMax must be ≥ 0';
        if (yTh <= 0) return 'SPM yThreshold must be a positive number';
    }
    return null;
}

const SAMPLE_FILE_URLS: Record<FileType, string> = {
    ANALYSIS: 'https://ags-qa-bucket.s3.dualstack.ap-south-1.amazonaws.com/Sample_Placement_Analysis_Data.xlsx',
    ACTIONABLES: 'https://ags-qa-bucket.s3.dualstack.ap-south-1.amazonaws.com/Sample_Placement_Actionables.xlsx',
    INSIGHT_DASHBOARD: 'https://ags-qa-bucket.s3.dualstack.ap-south-1.amazonaws.com/Template_Company_Landscape.xlsx',
};

const PlacementDataUpload = ({ instituteId }: Props) => {
    const [fileType, setFileType] = useState<FileType>('ANALYSIS');
    const [templateType, setTemplateType] = useState<TemplateType>('B_SCHOOL');
    const [yearFrom, setYearFrom] = useState('');
    const [yearTo, setYearTo] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [uploads, setUploads] = useState<any[]>([]);
    const [programs, setPrograms] = useState<ProgramOpt[]>([]);
    const [axisRows, setAxisRows] = useState<AxisRow[]>([]);
    const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
    const [axisDraft, setAxisDraft] = useState<Record<MatrixType, { xThreshold: number; xMax: number; yMax: number; yThreshold: number }>>({
        AHLI: { ...AXIS_DEFAULTS.AHLI },
        SPM: { ...AXIS_DEFAULTS.SPM },
    });
    const [axisError, setAxisError] = useState<string | null>(null);
    const [axisSaving, setAxisSaving] = useState(false);

    const refreshMeta = useCallback(async () => {
        const metaRes = await PlacementAnalyticsService.getMeta(instituteId);
        const meta = metaRes.data || {};
        setUploads(meta.uploads || []);
        const progs = (meta.programs?.programs || []) as ProgramOpt[];
        setPrograms(progs);
        setAxisRows(meta.axisConfig || []);
        if (meta.settings?.templateType) {
            setTemplateType(meta.settings.templateType);
        }
    }, [instituteId]);

    useEffect(() => {
        refreshMeta().catch((e) => setError(e?.response?.data?.message || e?.message || 'Failed to load upload meta'));
    }, [refreshMeta]);

    useEffect(() => {
        const next = { AHLI: { ...AXIS_DEFAULTS.AHLI }, SPM: { ...AXIS_DEFAULTS.SPM } };
        (['AHLI', 'SPM'] as MatrixType[]).forEach((mt) => {
            const row = axisRows.find(
                (r) =>
                    r.matrixType === mt &&
                    ((selectedProgramId == null && (r.placementProgramId == null || r.placementProgramId === undefined)) ||
                        Number(r.placementProgramId) === Number(selectedProgramId))
            );
            if (row) {
                next[mt] = {
                    xThreshold: Number(row.xThreshold),
                    xMax: Number(row.xMax ?? 0),
                    yMax: Number(row.yMax),
                    yThreshold: Number(row.yThreshold),
                };
            }
        });
        setAxisDraft(next);
        setAxisError(null);
    }, [axisRows, selectedProgramId]);

    const onUpload = async (e: FormEvent) => {
        e.preventDefault();
        if (!file) {
            setError('Choose an .xlsx file');
            return;
        }
        setUploading(true);
        setError(null);
        setMessage(null);
        try {
            if (fileType === 'ANALYSIS') {
                await PlacementAnalyticsService.upsertSettings(instituteId, { templateType });
            }
            const res = await PlacementAnalyticsService.upload(instituteId, file, {
                fileType,
                yearFrom: yearFrom || undefined,
                yearTo: yearTo || undefined,
                templateType: fileType === 'ANALYSIS' ? templateType : undefined,
            });
            setMessage(
                res.data?.status === 'SUCCESS'
                    ? `${fileType} uploaded successfully (upload #${res.data.uploadId})`
                    : 'Upload completed'
            );
            setFile(null);
            await refreshMeta();
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.errors?.join?.('\n') ||
                err?.message ||
                'Upload failed';
            setError(String(msg));
        } finally {
            setUploading(false);
        }
    };

    const saveAxis = async (matrixType: MatrixType) => {
        const d = axisDraft[matrixType];
        const localErr = validateAxisLocal(matrixType, d.xThreshold, d.xMax, d.yMax, d.yThreshold);
        if (localErr) {
            setAxisError(localErr);
            return;
        }
        setAxisSaving(true);
        setAxisError(null);
        try {
            await PlacementAnalyticsService.upsertAxisConfig(instituteId, {
                placementProgramId: selectedProgramId,
                matrixType,
                ...d,
            });
            const axisRes = await PlacementAnalyticsService.getAxisConfig(instituteId);
            setAxisRows(axisRes.data || []);
            setMessage(`${matrixType} axis saved`);
        } catch (err: any) {
            setAxisError(err?.response?.data?.message || err?.message || 'Axis save failed');
        } finally {
            setAxisSaving(false);
        }
    };

    const programLabel = useMemo(() => {
        const p = programs.find(
            (x) =>
                (selectedProgramId == null && (x.id == null || x.isAllPrograms)) ||
                Number(x.id) === Number(selectedProgramId) ||
                Number(x.placementProgramId) === Number(selectedProgramId)
        );
        return p?.programDisplayName || 'All Programs';
    }, [programs, selectedProgramId]);

    const sampleLabel =
        fileType === 'INSIGHT_DASHBOARD'
            ? 'Download sample INSIGHT_DASHBOARD file'
            : `Download sample ${fileType.toLowerCase()} file`;

    return (
        <div>
            <form onSubmit={onUpload} className="border rounded p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="font-18 font-600 mb-0">Upload placement file</h3>
                    <a
                        href={SAMPLE_FILE_URLS[fileType]}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-secondary btn-sm">
                        {sampleLabel}
                    </a>
                </div>
                <div className="row g-3">
                    <div className="col-md-3">
                        <label className="form-label font-13">File type</label>
                        <select
                            className="form-select"
                            value={fileType}
                            onChange={(e) => setFileType(e.target.value as FileType)}>
                            <option value="ANALYSIS">ANALYSIS</option>
                            <option value="ACTIONABLES">ACTIONABLES</option>
                            <option value="INSIGHT_DASHBOARD">INSIGHT_DASHBOARD</option>
                        </select>
                    </div>
                    {fileType === 'ANALYSIS' && (
                        <>
                            <div className="col-md-3">
                                <label className="form-label font-13">Template type</label>
                                <select
                                    className="form-select"
                                    value={templateType}
                                    onChange={(e) => setTemplateType(e.target.value as TemplateType)}>
                                    <option value="B_SCHOOL">B-School</option>
                                    <option value="ENGINEERING">Engineering</option>
                                    <option value="LAW_SCHOOL">Law School</option>
                                </select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label font-13">Year from</label>
                                <input
                                    className="form-control"
                                    value={yearFrom}
                                    onChange={(e) => setYearFrom(e.target.value)}
                                    placeholder="e.g. 2019"
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label font-13">Year to</label>
                                <input
                                    className="form-control"
                                    value={yearTo}
                                    onChange={(e) => setYearTo(e.target.value)}
                                    placeholder="e.g. 2024"
                                />
                            </div>
                        </>
                    )}
                    {fileType === 'INSIGHT_DASHBOARD' && (
                        <div className="col-12">
                            <div className="alert alert-light border py-2 font-13 mb-0">
                                Download the sample, fill the Company Landscape sheet, then upload. The file is stored in{' '}
                                <code>placement_file_upload</code> with <code>file_type = INSIGHT_DASHBOARD</code> and shown on the
                                Insights dashboard (Summary tab).
                            </div>
                        </div>
                    )}
                    <div className="col-md-4">
                        <label className="form-label font-13">.xlsx file</label>
                        <input
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="form-control"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                        <button type="submit" className="btn btn-primary w-100" disabled={uploading}>
                            {uploading ? 'Uploading…' : 'Upload'}
                        </button>
                    </div>
                </div>
            </form>

            {message && <div className="alert alert-success py-2 font-13">{message}</div>}
            {error && (
                <div className="alert alert-danger py-2 font-13" style={{ whiteSpace: 'pre-wrap' }}>
                    {error}
                </div>
            )}

            <div className="border rounded p-3 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="font-18 font-600 mb-0">Axis configuration — {programLabel}</h3>
                    <select
                        className="form-select w-auto"
                        value={selectedProgramId == null ? '' : String(selectedProgramId)}
                        onChange={(e) =>
                            setSelectedProgramId(e.target.value === '' ? null : Number(e.target.value))
                        }>
                        {programs.map((p) => (
                            <option
                                key={String(p.id ?? p.placementProgramId ?? 'all')}
                                value={p.id == null && !p.placementProgramId ? '' : String(p.id ?? p.placementProgramId)}>
                                {p.programDisplayName || p.programShortCode || 'Program'}
                            </option>
                        ))}
                    </select>
                </div>
                {axisError && <div className="alert alert-warning py-2 font-13">{axisError}</div>}
                {(['AHLI', 'SPM'] as MatrixType[]).map((mt) => (
                    <div key={mt} className="row g-2 align-items-end mb-3">
                        <div className="col-md-2">
                            <div className="font-600">{mt}</div>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label font-12">
                                {mt === 'AHLI' ? 'xThreshold (0–1 · 0.3=30%)' : 'xThreshold (offers)'}
                            </label>
                            <input
                                type="number"
                                step="any"
                                className="form-control"
                                value={axisDraft[mt].xThreshold}
                                onChange={(e) =>
                                    setAxisDraft((d) => ({
                                        ...d,
                                        [mt]: { ...d[mt], xThreshold: Number(e.target.value) },
                                    }))
                                }
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label font-12">xMax (0 = no cap)</label>
                            <input
                                type="number"
                                step="any"
                                className="form-control"
                                value={axisDraft[mt].xMax}
                                onChange={(e) =>
                                    setAxisDraft((d) => ({
                                        ...d,
                                        [mt]: { ...d[mt], xMax: Number(e.target.value) },
                                    }))
                                }
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label font-12">yMax</label>
                            <input
                                type="number"
                                step="any"
                                className="form-control"
                                value={axisDraft[mt].yMax}
                                onChange={(e) =>
                                    setAxisDraft((d) => ({
                                        ...d,
                                        [mt]: { ...d[mt], yMax: Number(e.target.value) },
                                    }))
                                }
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label font-12">yThreshold</label>
                            <input
                                type="number"
                                step="any"
                                className="form-control"
                                value={axisDraft[mt].yThreshold}
                                onChange={(e) =>
                                    setAxisDraft((d) => ({
                                        ...d,
                                        [mt]: { ...d[mt], yThreshold: Number(e.target.value) },
                                    }))
                                }
                            />
                        </div>
                        <div className="col-md-2">
                            <button
                                type="button"
                                className="btn btn-outline-primary"
                                disabled={axisSaving}
                                onClick={() => saveAxis(mt)}>
                                Save {mt}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="border rounded p-3">
                <h3 className="font-18 font-600 mb-3">Upload history</h3>
                <div className="table-responsive">
                    <table className="table table-sm align-middle">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Type</th>
                                <th>File</th>
                                <th>Years</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {uploads.map((u) => (
                                <tr key={u.id}>
                                    <td>{u.id}</td>
                                    <td>{u.fileType}</td>
                                    <td>{u.fileName}</td>
                                    <td>
                                        {u.yearFrom || '—'}–{u.yearTo || '—'}
                                    </td>
                                    <td>
                                        {u.status}
                                        {u.errorMessage ? (
                                            <div className="font-12 text-danger" title={u.errorMessage}>
                                                {String(u.errorMessage).slice(0, 80)}
                                            </div>
                                        ) : null}
                                    </td>
                                    <td>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '—'}</td>
                                    <td>
                                        {u.hasFile ? (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() =>
                                                    PlacementAnalyticsService.downloadUpload(
                                                        instituteId,
                                                        u.id,
                                                        u.fileName
                                                    )
                                                }>
                                                Download
                                            </button>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {!uploads.length && (
                                <tr>
                                    <td colSpan={7} className="text-muted text-center">
                                        No uploads yet
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default PlacementDataUpload;
